import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Loader2, RefreshCw, AlertCircle, X, PhoneCall, MailOpen, MousePointerClick,
  Send, CheckCircle2, Info,
} from 'lucide-react';

const API = '/api';
const HG_GREEN = '#48B64C';

const BUCKET_STYLE = {
  UP: 'bg-hg-green/10 text-hg-green',
  DOWN: 'bg-red-500/10 text-red-400',
  FLAT: 'bg-hg-border text-hg-text-dim',
  ZERO_QUIET: 'bg-yellow-500/10 text-yellow-400',
  BRAND_NEW: 'bg-blue-500/10 text-blue-300',
  NEW_WITH_PRIOR: 'bg-blue-500/10 text-blue-300',
};

function Tile({ icon: Icon, label, value, sub, accent = '#9a9a9a' }) {
  return (
    <div className="bg-hg-bg/60 border border-hg-border rounded-sm px-3 py-3">
      <div className="flex items-center gap-1.5">
        <Icon size={11} className="text-hg-text-dim" strokeWidth={1.5} />
        <div className="text-[9px] uppercase tracking-[0.18em] text-hg-text-dim font-sans">{label}</div>
      </div>
      <div className="font-display text-2xl text-hg-text tracking-wider mt-1" style={{ color: accent }}>{value}</div>
      {sub && <div className="text-[10px] text-hg-text-dim font-sans mt-0.5">{sub}</div>}
    </div>
  );
}

const YesNo = ({ on, color = HG_GREEN }) =>
  on ? <CheckCircle2 size={14} style={{ color }} className="inline" /> : <span className="text-hg-text-dim/40">—</span>;

export default function CheckinEngagement({ runId, monthLabel, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [sort, setSort] = useState({ key: 'engaged', dir: 'desc' });

  const fetchData = useCallback(async (refresh = false, signal) => {
    if (refresh) setRefreshing(true); else setLoading(true);
    try {
      const res = await axios.get(`${API}/fitter-checkin/runs/${runId}/engagement${refresh ? '?refresh=true' : ''}`, { signal });
      setData(res.data);
      setError('');
    } catch (e) {
      if (axios.isCancel?.(e) || e?.code === 'ERR_CANCELED') return;
      setError(e.response?.data?.detail || 'Failed to load engagement.');
    } finally {
      if (!signal?.aborted) { setLoading(false); setRefreshing(false); }
    }
  }, [runId]);

  useEffect(() => {
    const ctrl = new AbortController();
    fetchData(false, ctrl.signal);
    return () => ctrl.abort();
  }, [fetchData]);

  const s = data?.summary || {};
  const fitters = data?.fitters || [];
  const wants = data?.wants_to_talk || [];

  const engagedScore = (r) => (r.booked_a_call * 100) + (r.clicked * 10) + r.opened * 1 + r.delivered * 0.1;
  const sorted = [...fitters].sort((a, b) => {
    const dir = sort.dir === 'desc' ? -1 : 1;
    let av, bv;
    if (sort.key === 'engaged') { av = engagedScore(a); bv = engagedScore(b); }
    else if (sort.key === 'name') { av = (a.name || '').toLowerCase(); bv = (b.name || '').toLowerCase(); }
    else if (sort.key === 'bucket') { av = a.bucket || ''; bv = b.bucket || ''; }
    else { av = a[sort.key] || 0; bv = b[sort.key] || 0; }
    if (av < bv) return -1 * dir;
    if (av > bv) return 1 * dir;
    return 0;
  });
  const setSortKey = (key) =>
    setSort((p) => ({ key, dir: p.key === key && p.dir === 'desc' ? 'asc' : 'desc' }));

  const body = (
    <div className="space-y-4" data-testid="checkin-engagement">
      {/* header */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <PhoneCall size={15} className="text-hg-green" strokeWidth={1.5} />
          <h3 className="font-display text-lg text-hg-text tracking-wider">
            Check-in Performance{monthLabel ? ` — ${monthLabel}` : ''}
          </h3>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="engagement-refresh"
            onClick={() => fetchData(true)}
            disabled={refreshing || loading}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-sans border border-hg-border text-hg-text-dim rounded-sm hover:border-hg-green hover:text-hg-green transition-colors disabled:opacity-40"
          >
            <RefreshCw size={12} className={refreshing ? 'animate-spin' : ''} />
            {refreshing ? 'Refreshing…' : 'Refresh'}
          </button>
          {onClose && (
            <button onClick={onClose} className="text-hg-text-dim hover:text-hg-text" aria-label="Close"><X size={18} /></button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12"><Loader2 size={22} className="animate-spin text-hg-green" /></div>
      ) : error ? (
        <div className="bg-red-500/10 border border-red-500/20 rounded-sm p-4 flex items-center gap-3">
          <AlertCircle size={15} className="text-red-400" />
          <span className="text-sm text-red-400 font-sans">{error}</span>
        </div>
      ) : (
        <>
          {/* tiles */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <Tile icon={Send} label="Sent" value={s.sent || 0} />
            <Tile icon={CheckCircle2} label="Delivered" value={s.delivered || 0} accent="#5b9bd5" />
            <Tile icon={MailOpen} label="Opened" value={s.opened || 0} sub={`${s.open_rate || 0}% · directional`} accent={HG_GREEN} />
            <Tile icon={MousePointerClick} label="Clicked" value={s.clicked || 0} sub={`${s.click_rate || 0}% click rate`} accent="#76BB40" />
            <Tile icon={PhoneCall} label="Booked a call" value={s.booked_a_call || 0} accent={HG_GREEN} />
          </div>

          {/* wants to talk — the priority list */}
          <div className="bg-gradient-to-br from-hg-green/10 via-hg-surface to-hg-surface border border-hg-green/30 rounded-sm p-4" data-testid="wants-to-talk">
            <div className="flex items-center gap-2 mb-2">
              <PhoneCall size={13} className="text-hg-green" strokeWidth={1.5} />
              <div className="text-[10px] font-sans text-hg-green uppercase tracking-[0.2em]">Wants to talk — clicked Book-a-Call</div>
            </div>
            {wants.length === 0 ? (
              <p className="text-xs text-hg-text-dim font-sans">
                No Book-a-Call clicks yet. Clicks populate over the days after a send — check back.
              </p>
            ) : (
              <ul className="divide-y divide-hg-border/40">
                {wants.map((w) => (
                  <li key={w.fitter_id || w.email} className="flex items-center justify-between gap-3 py-2" data-testid={`wants-${w.email}`}>
                    <div className="min-w-0">
                      <div className="text-sm text-hg-text font-sans truncate">{w.name}</div>
                      <a href={`mailto:${w.email}`} className="text-xs text-hg-green font-sans hover:underline">{w.email}</a>
                    </div>
                    <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm flex-shrink-0 ${BUCKET_STYLE[w.bucket] || 'bg-hg-border text-hg-text-dim'}`}>{w.bucket}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* per-fitter table */}
          <div className="bg-hg-surface border border-hg-border rounded-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm font-sans">
                <thead>
                  <tr className="text-left text-[10px] text-hg-text-dim uppercase tracking-wider border-b border-hg-border">
                    {[
                      ['name', 'Fitter', 'left'], ['bucket', 'Bucket', 'left'],
                      ['delivered', 'Deliv', 'center'], ['opened', 'Open', 'center'],
                      ['clicked', 'Click', 'center'], ['booked_a_call', 'Booked', 'center'],
                      ['bounced', 'Bounce', 'center'],
                    ].map(([key, label, align]) => (
                      <th
                        key={key}
                        onClick={() => setSortKey(key)}
                        className={`px-3 py-2.5 font-normal cursor-pointer hover:text-hg-text select-none text-${align} ${sort.key === key ? 'text-hg-green' : ''}`}
                      >{label}{sort.key === key ? (sort.dir === 'desc' ? ' ↓' : ' ↑') : ''}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sorted.length === 0 ? (
                    <tr><td colSpan={7} className="text-center text-hg-text-dim py-10 text-xs">No sent check-ins in this run.</td></tr>
                  ) : sorted.map((r) => (
                    <tr key={r.fitter_id || r.email} className={`border-b border-hg-border/40 hover:bg-hg-bg/40 ${r.booked_a_call ? 'bg-hg-green/5' : ''}`} data-testid={`fitter-row-${r.email}`}>
                      <td className="px-3 py-2.5 text-hg-text">
                        <div className="truncate max-w-[180px]">{r.name}</div>
                        <div className="text-[11px] text-hg-text-dim truncate max-w-[180px]">{r.email}</div>
                      </td>
                      <td className="px-3 py-2.5">
                        <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm ${BUCKET_STYLE[r.bucket] || 'bg-hg-border text-hg-text-dim'}`}>{r.bucket}</span>
                      </td>
                      <td className="px-3 py-2.5 text-center"><YesNo on={r.delivered} color="#5b9bd5" /></td>
                      <td className="px-3 py-2.5 text-center">{r.opened ? <span className="text-hg-green">{r.opens_count > 1 ? `${r.opens_count}×` : ''}<MailOpen size={14} className="inline" /></span> : <span className="text-hg-text-dim/40">—</span>}</td>
                      <td className="px-3 py-2.5 text-center"><YesNo on={r.clicked} color="#76BB40" /></td>
                      <td className="px-3 py-2.5 text-center">{r.booked_a_call ? <PhoneCall size={14} className="inline text-hg-green" /> : <span className="text-hg-text-dim/40">—</span>}</td>
                      <td className="px-3 py-2.5 text-center">{r.bounced ? <span className="text-red-400 text-xs">bounced</span> : <span className="text-hg-text-dim/40">—</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* note */}
          <div className="flex items-start gap-2 text-[11px] text-hg-text-dim font-sans">
            <Info size={12} className="mt-0.5 flex-shrink-0" />
            <span>{data?.note}</span>
          </div>
        </>
      )}
    </div>
  );

  if (onClose) {
    return (
      <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/60 p-4 overflow-y-auto" onClick={onClose}>
        <div className="bg-hg-surface border border-hg-border rounded-sm p-5 w-full max-w-4xl mt-8" onClick={(e) => e.stopPropagation()}>
          {body}
        </div>
      </div>
    );
  }
  return <div className="bg-hg-surface border border-hg-border rounded-sm p-5">{body}</div>;
}
